import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Building2, Users, Briefcase, Plus, Power, Archive, Pencil, Trash2, CheckCircle2, Mail, ShieldAlert, AlertTriangle, KeyRound, RotateCcw, Settings, Activity, BarChart3, MapPin } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { IntroCover } from "@/components/IntroCover";
import { peekIntro, clearIntro } from "@/lib/introFlag";
import { welcomeToast } from "@/lib/welcome";
import { useAuth } from "@/context/AuthContext";
import { AppaltoDialog } from "@/components/admin/AppaltoDialog";
import { AccountDialog } from "@/components/admin/AccountDialog";
import { ConfigurazioniSection } from "@/components/admin/ConfigurazioniSection";
import { SystemHealthSection } from "@/components/admin/SystemHealthSection";
import { StatisticheSection } from "@/components/admin/StatisticheSection";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SearchBox } from "@/components/SearchBox";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { serviceDisplayName } from "@/lib/services";

const RESET_LABELS = {
  workers: "account non-Admin", appalti: "appalti",
  work_entries: "timbrature", leave_requests: "richieste giustificativi",
  indirect_hours: "ore indirette", indirect_schedules: "pianificazioni ore indirette",
  monthly_closures: "chiusure mensili", school_users: "utenti / gruppi",
  service_entities: "anagrafiche servizi", schools: "scuole", sedi_operative: "sedi operative",
  pre_post_locations: "sedi pre/post", camps: "centri estivi",
  agenda_slots: "voci agenda", substitutions: "sostituzioni",
  communications: "comunicazioni", relation_instances: "relazioni",
  relation_templates: "modelli relazioni", relation_publications: "pubblicazioni relazioni",
  attendance_registers: "registri presenze", notifications: "notifiche",
  mail_views: "anteprime mail", stamp_alerts: "alert timbrature",
  consuntivo_checks: "check consuntivo", recent_registers: "registri frequenti",
  email_logs: "log email", push_subscriptions: "iscrizioni push", push_sent: "push inviate",
  password_reset_tokens: "token reset password", system_metrics_samples: "campioni telemetria",
  audit_log: "voci di audit", app_config: "configurazioni",
};

function ResetDataSection() {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const close = () => { setOpen(false); setPwd(""); setResult(null); setError(""); };

  const confirmReset = async () => {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-operational-data", { password: pwd });
      setResult(data); setPwd("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Reset non riuscito");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="mt-10 rounded-xl border border-rose-200 bg-rose-50/50 p-5" data-testid="reset-data-section">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-rose-100 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5 text-rose-600" /></div>
          <div className="flex-1">
            <p className="font-heading font-bold text-rose-800">Reset totale · nuova installazione</p>
            <p className="text-sm text-rose-700/80 mt-0.5">Riporta Orbit allo stato iniziale: elimina <b>tutti</b> i dati (account Ufficio e Lavoratori, appalti, timbrature, agende, comunicazioni, notifiche, log…). Restano solo gli account <b>Amministratore</b> e il catalogo tecnico dei servizi.</p>
          </div>
          <Button variant="destructive" onClick={() => setOpen(true)} data-testid="reset-data-btn" className="shrink-0">
            <Trash2 className="h-4 w-4 mr-1.5" /> Reset totale
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading text-rose-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Attenzione</DialogTitle></DialogHeader>
          {!result ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">Questa operazione riporta Orbit allo stato di <b>nuova installazione</b>: elimina <b>definitivamente</b> tutti i dati (account Ufficio/Lavoratori, appalti, timbrature, agende, registri, comunicazioni, relazioni, notifiche, MailView, log e telemetria).</p>
              <p className="text-sm text-slate-500">Resteranno esclusivamente gli account <b>Amministratore</b> (con password invariata) e il catalogo tecnico dei servizi.</p>
              <div className="space-y-1.5">
                <Label>Password amministratore</Label>
                <Input type="password" data-testid="reset-password-input" value={pwd} onChange={(e) => setPwd(e.target.value)} autoComplete="off" className="h-12" />
              </div>
              {error && <p className="text-sm text-destructive" data-testid="reset-error">{error}</p>}
              <DialogFooter>
                <Button variant="ghost" onClick={close}>Annulla</Button>
                <Button variant="destructive" onClick={confirmReset} disabled={busy || !pwd} data-testid="reset-confirm-btn">{busy ? "Reset in corso…" : "Conferma reset"}</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid="reset-result">
              <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Reset completato</p>
              <ul className="text-sm text-slate-700 space-y-1">
                {Object.entries(result.deleted || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                  <li key={k} data-testid={`reset-count-${k}`}>{n} {RESET_LABELS[k] || k} eliminate</li>
                ))}
                {result.total_deleted === 0 && <li className="text-slate-500">Nessun dato operativo da eliminare.</li>}
              </ul>
              {result.errors && <p className="text-xs text-destructive">Alcune collection hanno riportato errori: {Object.keys(result.errors).join(", ")}</p>}
              <DialogFooter><Button onClick={close} data-testid="reset-done-btn">Chiudi</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ResetAppaltoSection({ appalti = [], onDone }) {
  const [open, setOpen] = useState(false);
  const [appaltoId, setAppaltoId] = useState("");
  const [pwd, setPwd] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const close = () => { setOpen(false); setAppaltoId(""); setPwd(""); setAck(false); setResult(null); setError(""); };
  const selectedNome = appalti.find((a) => a.id === appaltoId)?.nome || "";

  const confirmReset = async () => {
    setError(""); setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-appalto", { appalto_id: appaltoId, password: pwd });
      setResult(data); setPwd(""); setAck(false);
      onDone && onDone();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Reset appalto non riuscito");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/50 p-5" data-testid="reset-appalto-section">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5 text-amber-600" /></div>
          <div className="flex-1">
            <p className="font-heading font-bold text-amber-800">Reset specifico · un solo Appalto</p>
            <p className="text-sm text-amber-700/80 mt-0.5">Riporta a stato pulito <b>solo l'Appalto selezionato</b>: elimina i suoi dati operativi/configurativi (timbrature, agende, registri, sedi, servizi, account collegati solo a questo appalto…). Gli <b>altri Appalti restano invariati</b>. L'Appalto verrà rimosso e potrà essere ricreato.</p>
          </div>
          <Button variant="outline" onClick={() => setOpen(true)} data-testid="reset-appalto-btn" className="shrink-0 border-amber-300 text-amber-800 hover:bg-amber-100">
            <Trash2 className="h-4 w-4 mr-1.5" /> Reset appalto
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && close()}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading text-amber-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Reset specifico Appalto</DialogTitle></DialogHeader>
          {!result ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-700">L'operazione elimina <b>definitivamente</b> tutti i dati del <b>solo Appalto selezionato</b> e l'Appalto stesso. Gli altri Appalti (account, timbrature, configurazioni) <b>non vengono toccati</b>.</p>
              <div className="space-y-1.5">
                <Label>Appalto da azzerare</Label>
                <Select value={appaltoId} onValueChange={setAppaltoId}>
                  <SelectTrigger data-testid="reset-appalto-select" className="h-11"><SelectValue placeholder="Seleziona l'appalto…" /></SelectTrigger>
                  <SelectContent>{appalti.map((a) => (<SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Password amministratore</Label>
                <Input type="password" data-testid="reset-appalto-password-input" value={pwd} onChange={(e) => setPwd(e.target.value)} autoComplete="off" className="h-12" />
              </div>
              <label className="flex items-start gap-2.5 rounded-lg bg-amber-50 border border-amber-200 p-3 cursor-pointer">
                <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="reset-appalto-ack" className="mt-0.5 h-4 w-4" />
                <span className="text-sm text-slate-700">Confermo di voler eliminare definitivamente tutti i dati di <b>{selectedNome || "questo appalto"}</b>.</span>
              </label>
              {error && <p className="text-sm text-destructive" data-testid="reset-appalto-error">{error}</p>}
              <DialogFooter>
                <Button variant="ghost" onClick={close}>Annulla</Button>
                <Button variant="destructive" onClick={confirmReset} disabled={busy || !appaltoId || !pwd || !ack} data-testid="reset-appalto-confirm-btn">{busy ? "Reset in corso…" : "Conferma reset appalto"}</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid="reset-appalto-result">
              <p className="text-sm font-semibold text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Reset completato · {result.appalto_nome}</p>
              <ul className="text-sm text-slate-700 space-y-1">
                {Object.entries(result.deleted || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                  <li key={k} data-testid={`reset-appalto-count-${k}`}>{n} {RESET_LABELS[k] || k} eliminate</li>
                ))}
                {result.total_deleted === 0 && <li className="text-slate-500">Nessun dato da eliminare per questo appalto.</li>}
                {result.info?.accounts_scollegati > 0 && <li className="text-slate-500">{result.info.accounts_scollegati} account condivisi scollegati (conservati per gli altri appalti)</li>}
              </ul>
              {result.errors && <p className="text-xs text-destructive">Alcune collection hanno riportato errori: {Object.keys(result.errors).join(", ")}</p>}
              <DialogFooter><Button onClick={close} data-testid="reset-appalto-done-btn">Chiudi</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


const STATO_BADGE = {
  attivo: "bg-success/10 text-success",
  disattivato: "bg-amber-100 text-amber-700",
  archiviato: "bg-slate-200 text-slate-600",
};

// Indirizzo di riferimento geolocalizzazione (solo configurazione; conservato anche con flag OFF).
function GeoAddressField({ appaltoId, slug, value, active, tolerance, hasCoords, geocodedLabel }) {
  const [draft, setDraft] = useState(value || "");
  const [saved, setSaved] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const [coords, setCoords] = useState({ ok: !!hasCoords, label: geocodedLabel || "" });
  useEffect(() => { setDraft(value || ""); setSaved(value || ""); }, [value]);
  useEffect(() => { setCoords({ ok: !!hasCoords, label: geocodedLabel || "" }); }, [hasCoords, geocodedLabel]);
  const dirty = draft.trim() !== saved.trim();
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/appalti/${appaltoId}/geolocation-address`, { service_type: slug, address: draft });
      setSaved(data.geolocation_address); setDraft(data.geolocation_address);
      setCoords({ ok: data.geolocation_has_coords, label: data.geolocation_geocoded_label || "" });
      if (!data.geolocation_address) toast.success("Indirizzo rimosso");
      else if (data.geolocation_has_coords) toast.success("Indirizzo salvato e localizzato");
      else toast.warning("Indirizzo salvato ma non localizzato: verifica l'indirizzo");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="mt-1.5 pt-1.5 border-t border-slate-100">
    <div className="flex items-center gap-2">
      <MapPin className={`h-3.5 w-3.5 shrink-0 ${active ? "text-success" : "text-slate-400"}`} />
      <Input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={300} placeholder="Indirizzo"
        onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(); }}
        data-testid={`geolocation-address-${appaltoId}-${slug}`} className="h-8 text-sm bg-white" />
      {dirty && (
        <Button size="sm" className="h-8 rounded-lg shrink-0" disabled={saving} onClick={save}
          data-testid={`geolocation-address-save-${appaltoId}-${slug}`}>{saving ? "…" : "Salva"}</Button>
      )}
    </div>
    {saved && (
      <p className={`text-[10px] mt-1 ml-5 ${coords.ok ? "text-slate-500" : "text-amber-600"}`} data-testid={`geolocation-coords-status-${appaltoId}-${slug}`}>
        {coords.ok ? `Sede localizzata: ${coords.label}` : "Indirizzo non localizzato: il controllo geografico non viene applicato"}
      </p>
    )}
    <GeoToleranceField appaltoId={appaltoId} slug={slug} value={tolerance} />
    </div>
  );
}

function GeoToleranceField({ appaltoId, slug, value }) {
  const [draft, setDraft] = useState(String(value || 60));
  const [saved, setSaved] = useState(String(value || 60));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(String(value || 60)); setSaved(String(value || 60)); }, [value]);
  const valid = /^[0-9]+$/.test(draft) && Number(draft) >= 1 && Number(draft) <= 5000;
  const dirty = draft !== saved;
  const save = async () => {
    if (!valid) return toast.error("Inserisci un numero positivo di metri (1–5000)");
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/appalti/${appaltoId}/geolocation-tolerance`, { service_type: slug, tolerance_m: Number(draft) });
      setSaved(String(data.geolocation_tolerance_m)); toast.success("Tolleranza salvata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-2 mt-1.5 ml-5">
      <span className="text-xs text-slate-600">Tolleranza:</span>
      <Input value={draft} onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" maxLength={4}
        onKeyDown={(e) => { if (e.key === "Enter" && dirty) save(); }}
        data-testid={`geolocation-tolerance-${appaltoId}-${slug}`} className="h-8 w-20 text-sm bg-white" />
      <span className="text-xs text-slate-600">metri</span>
      {dirty && (
        <Button size="sm" className="h-8 rounded-lg shrink-0" disabled={saving || !valid} onClick={save}
          data-testid={`geolocation-tolerance-save-${appaltoId}-${slug}`}>{saving ? "…" : "Salva"}</Button>
      )}
    </div>
  );
}

export default function AdminHome() {
  const { user } = useAuth();
  const [appalti, setAppalti] = useState([]);
  const [intro, setIntro] = useState(() => peekIntro());
  useEffect(() => { clearIntro(); }, []);
  const [services, setServices] = useState([]);
  const [ufficio, setUfficio] = useState([]);
  const [lavoratori, setLavoratori] = useState([]);
  const [deletedAccounts, setDeletedAccounts] = useState([]);
  const [accountSection, setAccountSection] = useState("lavoratori");
  const [accountSearch, setAccountSearch] = useState("");
  const [filterAppalto, setFilterAppalto] = useState("all");
  const [appaltoDialog, setAppaltoDialog] = useState(null); // {mode, appalto?}
  const [accountDialog, setAccountDialog] = useState(null); // {role, account?}

  const load = useCallback(async () => {
    const [a, s, u, l, d] = await Promise.all([
      api.get("/admin/appalti"),
      api.get("/admin/services"),
      api.get("/admin/accounts?role=UFFICIO"),
      api.get("/admin/accounts?role=LAVORATORE"),
      api.get("/admin/accounts?deleted=true"),
    ]);
    setAppalti(a.data);
    setServices(s.data.filter((x) => x.stato === "attivo"));
    setUfficio(u.data);
    setLavoratori(l.data);
    setDeletedAccounts(d.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Riallinea lo stato reale del backend quando l'Admin ritorna sulla scheda
  // (es. dopo che un account completa l'attivazione da un'altra sessione).
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [load]);

  const activeAppalti = useMemo(() => appalti.filter((a) => a.stato === "attivo"), [appalti]);
  const appaltoName = useCallback((id) => appalti.find((a) => a.id === id)?.nome || "—", [appalti]);

  const filterAccounts = (list) => filterAppalto === "all" ? list : list.filter((w) => (w.appalto_ids || []).includes(filterAppalto));
  const matchSearch = (w) => {
    const q = accountSearch.trim().toLowerCase();
    if (!q) return true;
    return [w.nome, w.cognome, w.email].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
  };
  const shown = (list) => filterAccounts(list).filter(matchSearch);
  const shownAppalti = filterAppalto === "all" ? appalti : appalti.filter((a) => a.id === filterAppalto);

  const setStato = async (id, stato) => {
    try { await api.patch(`/admin/appalti/${id}/stato`, { stato }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleAnomalyCheck = async (appaltoId, serviceSlug, active) => {
    try {
      await api.patch(`/admin/appalti/${appaltoId}/anomaly-check`, { service_type: serviceSlug, active });
      toast.success(active ? "Controllo errori riattivato" : "Controllo errori sospeso");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleGeolocation = async (appaltoId, serviceSlug, active) => {
    try {
      await api.patch(`/admin/appalti/${appaltoId}/geolocation`, { service_type: serviceSlug, active });
      toast.success(active ? "Geolocalizzazione attivata" : "Geolocalizzazione disattivata");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleAccount = async (w) => {
    try { await api.patch(`/workers/${w.id}/status`); toast.success("Stato account aggiornato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteAccount = async (w) => {
    if (!window.confirm(`Eliminare l'account di ${w.nome} ${w.cognome}? I dati storici restano conservati.`)) return;
    try { const { data } = await api.delete(`/workers/${w.id}`); toast.success(data.message); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const restoreAccount = async (w) => {
    try { const { data } = await api.post(`/workers/${w.id}/restore`); toast.success(data.message); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const resendInvite = async (w) => {
    try { await api.post(`/workers/${w.id}/resend-activation`); toast.success("Invito di attivazione reinviato"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendResetPassword = async (w) => {
    try {
      const { data } = await api.post(`/workers/${w.id}/send-reset`);
      if (data?.email_status === "sent") toast.success("E-mail di reimpostazione password inviata al lavoratore");
      else toast.warning("Richiesta registrata, ma l'e-mail non è stata inviata");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const AccountCard = ({ w }) => {
    const isActive = w.account_attivo || w.stato_invito === "attivo";
    return (
    <Card className="rounded-xl border-slate-200 shadow-sm" data-testid={`account-card-${w.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div>
            <p className="font-semibold text-slate-800">{w.nome} {w.cognome}</p>
            <p className="text-sm text-slate-500">{w.ruolo_professionale} · {w.email}</p>
            {(w.available_profiles || []).length > 1 && (
              <Badge className="bg-indigo-50 text-indigo-700 border-0 text-[10px] mt-1" data-testid={`account-hybrid-${w.id}`}>Account ibrido · Ufficio + Lavoratore</Badge>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`account-appalti-${w.id}`}>
              {(w.appalto_ids || []).map((id) => <Badge key={id} className="bg-primary/10 text-primary border-0 text-[10px]">{appaltoName(id)}</Badge>)}
              {(w.appalto_ids || []).length === 0 && <span className="text-xs text-slate-400">Nessun appalto</span>}
            </div>
          </div>
          <Badge className={`border-0 text-[10px] ${isActive ? "bg-success/10 text-success" : w.attivato ? "bg-destructive/10 text-destructive" : "bg-amber-100 text-amber-700"}`}>
            {isActive ? "Attivo" : w.attivato ? "Disattivato" : (w.stato_invito === "invito_non_inviato" ? "Invito non inviato" : "Invito inviato")}
          </Badge>
        </div>
        <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">
          <Button variant="outline" size="sm" className="rounded-lg" data-testid={`account-edit-${w.id}`}
            onClick={() => setAccountDialog({ role: w.profilo_accesso, account: w })}><Pencil className="h-4 w-4 mr-1.5" />Modifica</Button>
          {!w.attivato && !isActive && (
            <Button variant="outline" size="sm" className="rounded-lg" data-testid={`account-resend-${w.id}`}
              onClick={() => resendInvite(w)}><Mail className="h-4 w-4 mr-1.5" />Reinvia invito</Button>
          )}
          <Button variant="outline" size="sm" className="rounded-lg" data-testid={`account-toggle-${w.id}`}
            onClick={() => toggleAccount(w)}><Power className="h-4 w-4 mr-1.5" />{isActive ? "Disattiva" : "Attiva"}</Button>
          {isActive && (
            <Button variant="outline" size="sm" className="rounded-lg" data-testid={`account-reset-${w.id}`}
              onClick={() => sendResetPassword(w)}><KeyRound className="h-4 w-4 mr-1.5" />Reimposta password</Button>
          )}
          <Button variant="outline" size="sm" className="rounded-lg text-destructive border-destructive/30 hover:bg-destructive/5"
            data-testid={`account-delete-${w.id}`} onClick={() => deleteAccount(w)}><Trash2 className="h-4 w-4 mr-1.5" />Elimina</Button>
        </div>
      </CardContent>
    </Card>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      {intro && <IntroCover onDone={() => { setIntro(false); welcomeToast(user?.nome); }} />}
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Area Amministratore</h1>
            <p className="text-sm text-slate-500">Gestione globale di appalti e account</p>
          </div>
          <div className="w-56">
            <Select value={filterAppalto} onValueChange={setFilterAppalto}>
              <SelectTrigger data-testid="admin-appalto-filter" className="h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli appalti</SelectItem>
                {appalti.map((a) => <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <Tabs defaultValue="appalti">
          <TabsList className="mb-4 flex w-full max-w-full justify-start overflow-x-auto [&>button]:shrink-0">
            <TabsTrigger value="appalti" data-testid="tab-appalti"><Building2 className="h-4 w-4 mr-1.5" />Appalti</TabsTrigger>
            <TabsTrigger value="account" data-testid="tab-account"><Users className="h-4 w-4 mr-1.5" />Account</TabsTrigger>
            <TabsTrigger value="configurazioni" data-testid="tab-configurazioni"><Settings className="h-4 w-4 mr-1.5" />Configurazioni</TabsTrigger>
            <TabsTrigger value="sistema" data-testid="tab-sistema"><Activity className="h-4 w-4 mr-1.5" />Stato del sistema</TabsTrigger>
            <TabsTrigger value="statistiche" data-testid="tab-statistiche"><BarChart3 className="h-4 w-4 mr-1.5" />Statistiche</TabsTrigger>
          </TabsList>

          <TabsContent value="appalti" className="space-y-3">
            <Button onClick={() => setAppaltoDialog({ mode: "create" })} data-testid="appalto-create-btn" className="rounded-lg font-semibold"><Plus className="h-4 w-4 mr-1.5" />Nuovo appalto</Button>
            {shownAppalti.length === 0 && <Card className="rounded-xl"><CardContent className="p-6 text-center text-slate-500 text-sm">Nessun appalto. Creane uno per iniziare.</CardContent></Card>}
            {shownAppalti.map((a) => (
              <Card key={a.id} className="rounded-xl border-slate-200 shadow-sm" data-testid={`appalto-card-${a.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-800">{a.nome}</p>
                      {a.descrizione && <p className="text-sm text-slate-500">{a.descrizione}</p>}
                      {(a.servizi || []).length === 0 ? (
                        <span className="text-xs text-slate-400 mt-2 inline-block">Nessun servizio abilitato</span>
                      ) : (
                        <div className="mt-2 space-y-1.5" data-testid={`appalto-services-${a.id}`}>
                          {(a.servizi || []).map((s) => {
                            const on = s.anomaly_check_active !== false;
                            const geo = s.geolocation_active === true;
                            return (
                              <div key={s.id} className="rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1.5">
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <span className="text-sm font-medium text-slate-700">{serviceDisplayName(s)}</span>
                                  <span data-testid={`anomaly-status-${a.id}-${s.slug}`}
                                    className={`ml-2 text-[10px] font-semibold ${on ? "text-success" : "text-amber-600"}`}>
                                    {on ? "Controllo attivo" : "Controllo sospeso"}
                                  </span>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                  <div className="flex items-center gap-1.5" title="Controllo errori timbratura">
                                    <ShieldAlert className={`h-3.5 w-3.5 ${on ? "text-success" : "text-amber-500"}`} />
                                    <Switch checked={on} data-testid={`anomaly-toggle-${a.id}-${s.slug}`}
                                      onCheckedChange={(v) => toggleAnomalyCheck(a.id, s.slug, v)} />
                                  </div>
                                  <div className="flex items-center gap-1.5" title={`Geolocalizzazione ${geo ? "ON" : "OFF"}`}>
                                    <MapPin className={`h-3.5 w-3.5 ${geo ? "text-success" : "text-slate-400"}`} />
                                    <Switch checked={geo} data-testid={`geolocation-toggle-${a.id}-${s.slug}`}
                                      onCheckedChange={(v) => toggleGeolocation(a.id, s.slug, v)} />
                                  </div>
                                </div>
                              </div>
                              {s.geolocation_address !== undefined && (
                                <GeoAddressField appaltoId={a.id} slug={s.slug} value={s.geolocation_address} active={geo}
                                  tolerance={s.geolocation_tolerance_m} hasCoords={s.geolocation_has_coords} geocodedLabel={s.geolocation_geocoded_label} />
                              )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <Badge className={`border-0 text-[10px] ${STATO_BADGE[a.stato]}`} data-testid={`appalto-stato-${a.id}`}>{a.stato}</Badge>
                  </div>
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">
                    <Button variant="outline" size="sm" className="rounded-lg" data-testid={`appalto-edit-${a.id}`} onClick={() => setAppaltoDialog({ mode: "edit", appalto: a })}><Pencil className="h-4 w-4 mr-1.5" />Modifica</Button>
                    {a.stato !== "attivo" && <Button variant="outline" size="sm" className="rounded-lg text-success border-success/30" data-testid={`appalto-activate-${a.id}`} onClick={() => setStato(a.id, "attivo")}><CheckCircle2 className="h-4 w-4 mr-1.5" />Attiva</Button>}
                    {a.stato === "attivo" && <Button variant="outline" size="sm" className="rounded-lg" data-testid={`appalto-deactivate-${a.id}`} onClick={() => setStato(a.id, "disattivato")}><Power className="h-4 w-4 mr-1.5" />Disattiva</Button>}
                    {a.stato !== "archiviato" && <Button variant="outline" size="sm" className="rounded-lg text-slate-600" data-testid={`appalto-archive-${a.id}`} onClick={() => setStato(a.id, "archiviato")}><Archive className="h-4 w-4 mr-1.5" />Archivia</Button>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="account" className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="account-subnav">
              {[{ k: "lavoratori", label: "Lavoratori", Icon: Users }, { k: "ufficio", label: "Ufficio", Icon: Briefcase }, { k: "eliminati", label: "Eliminati", Icon: Trash2 }].map(({ k, label, Icon }) => (
                <button key={k} data-testid={`account-sub-${k}`} onClick={() => { setAccountSection(k); setAccountSearch(""); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all ${accountSection === k ? "bg-primary text-white border-primary shadow-sm" : "bg-white text-slate-600 border-slate-200 hover:border-primary/40"}`}>
                  <Icon className="h-4 w-4" />{label}
                </button>
              ))}
            </div>
            <div className="max-w-md">
              <SearchBox value={accountSearch} onChange={setAccountSearch} placeholder="Cerca per nome, cognome o email…" testid="account-search" />
            </div>

            {accountSection === "ufficio" && (
              <div className="space-y-3" data-testid="account-pane-ufficio">
                <Button onClick={() => setAccountDialog({ role: "UFFICIO" })} data-testid="office-create-btn" className="rounded-lg font-semibold" disabled={activeAppalti.length === 0}><Plus className="h-4 w-4 mr-1.5" />Nuovo account Ufficio</Button>
                {activeAppalti.length === 0 && <p className="text-xs text-amber-600">Crea prima un appalto attivo per poter associare gli account.</p>}
                {shown(ufficio).length === 0 && <Card className="rounded-xl"><CardContent className="p-6 text-center text-slate-500 text-sm">Nessun account Ufficio.</CardContent></Card>}
                {shown(ufficio).map((w) => <AccountCard key={w.id} w={w} />)}
              </div>
            )}

            {accountSection === "lavoratori" && (
              <div className="space-y-3" data-testid="account-pane-lavoratori">
                <Button onClick={() => setAccountDialog({ role: "LAVORATORE" })} data-testid="lavoratore-create-btn" className="rounded-lg font-semibold" disabled={activeAppalti.length === 0}><Plus className="h-4 w-4 mr-1.5" />Nuovo lavoratore</Button>
                {activeAppalti.length === 0 && <p className="text-xs text-amber-600">Crea prima un appalto attivo per poter associare gli account.</p>}
                {shown(lavoratori).length === 0 && <Card className="rounded-xl"><CardContent className="p-6 text-center text-slate-500 text-sm">Nessun lavoratore.</CardContent></Card>}
                {shown(lavoratori).map((w) => <AccountCard key={w.id} w={w} />)}
              </div>
            )}

            {accountSection === "eliminati" && (
              <div className="space-y-3" data-testid="account-pane-eliminati">
                <p className="text-sm text-slate-500">Account eliminati: non possono accedere e non compaiono nelle liste operative. I dati storici e le associazioni sono conservati per la rendicontazione.</p>
                {shown(deletedAccounts).length === 0 && <Card className="rounded-xl"><CardContent className="p-6 text-center text-slate-500 text-sm" data-testid="deleted-empty">Nessun account eliminato.</CardContent></Card>}
                {shown(deletedAccounts).map((w) => (
                  <Card key={w.id} className="rounded-xl border-slate-200 shadow-sm opacity-90" data-testid={`deleted-card-${w.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-semibold text-slate-800">{w.nome} {w.cognome}</p>
                          <p className="text-sm text-slate-500">{w.ruolo_professionale} · {w.email}</p>
                          <p className="text-xs text-slate-400 mt-1">{w.profilo_accesso === "UFFICIO" ? "Account Ufficio" : "Lavoratore"}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {(w.appalto_ids || []).map((id) => <Badge key={id} className="bg-slate-100 text-slate-500 border-0 text-[10px]">{appaltoName(id)}</Badge>)}
                          </div>
                        </div>
                        <Badge className="border-0 text-[10px] bg-slate-200 text-slate-600" data-testid={`deleted-badge-${w.id}`}>Eliminato</Badge>
                      </div>
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <Button variant="outline" size="sm" className="rounded-lg text-success border-success/30 hover:bg-success/5"
                          data-testid={`account-restore-${w.id}`} onClick={() => restoreAccount(w)}>
                          <RotateCcw className="h-4 w-4 mr-1.5" />Riattiva
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="configurazioni">
            <ConfigurazioniSection />
          </TabsContent>
          <TabsContent value="sistema">
            <SystemHealthSection />
          </TabsContent>
          <TabsContent value="statistiche">
            <StatisticheSection />
          </TabsContent>
        </Tabs>

        <ResetDataSection />
        <ResetAppaltoSection appalti={appalti} onDone={load} />
      </main>

      {appaltoDialog && (
        <AppaltoDialog services={services} appalto={appaltoDialog.appalto}
          onClose={() => setAppaltoDialog(null)} onSaved={() => { setAppaltoDialog(null); load(); }} />
      )}
      {accountDialog && (
        <AccountDialog role={accountDialog.role} appalti={activeAppalti} account={accountDialog.account}
          onClose={() => setAccountDialog(null)} onSaved={() => { setAccountDialog(null); load(); }} />
      )}
    </div>
  );
}
